// Notes cell for the SmartCharger Manage table. The inline field is cramped for
// real notes, so the cell shows a truncated preview and opens a roomy modal
// (Notepad-tab feel) for comfortable multi-line editing.
//   • click the cell → modal with a large textarea
//   • Ctrl/Cmd+Enter saves · Esc or backdrop closes · empty clears the note

import React, { useEffect, useRef, useState } from "react";
import { buttonClass } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { TextArea } from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';

const NotesCell: React.FC<{
	value: string | null;
	onSave: (v: string | null) => Promise<void>;
}> = ({ value, onSave }) => {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState<string>(value ?? "");
	const [saving, setSaving] = useState(false);
	const taRef = useRef<HTMLTextAreaElement>(null);

	// Seed the draft when the modal opens; the Dialog moves focus to the textarea.
	useEffect(() => {
		if (!open) return;
		setDraft(value ?? "");
	}, [open, value]);

	const save = async () => {
		if (saving) return;
		const next = draft.trim() === "" ? null : draft;
		if ((next ?? null) === (value ?? null)) {
			setOpen(false);
			return;
		}
		setSaving(true);
		try {
			await onSave(next);
			setOpen(false);
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Tooltip label={value ?? ""} truncated>
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="text-left text-sm text-on-surface hover:text-primary max-w-[220px] truncate block w-full"
				>
					{value ? value : <span className="italic text-on-surface-disabled">add note…</span>}
				</button>
			</Tooltip>
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				title="Notes"
				label="Edit notes"
				size="lg"
				dirty={open && draft !== (value ?? "")}
				initialFocus={taRef}
				footer={
					<>
						<span className="mr-auto text-xs text-on-surface-variant">Ctrl/⌘+Enter to save, Esc to close</span>
						<button
							type="button"
							onClick={() => setOpen(false)}
							className={buttonClass('quiet', 'md')}
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={save}
							disabled={saving}
							className={buttonClass('filled', 'md')}
						>
							{saving ? "Saving…" : "Save"}
						</button>
					</>
				}
			>
				<TextArea
					ref={taRef}
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
							e.preventDefault();
							save();
						}
					}}
					rows={12}
					placeholder="Notes about this charger…"
					className="font-mono"
				/>
			</Dialog>
		</>
	);
};

export default NotesCell;
